import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ProductCard } from '../components/ProductCard';
import { ArrowRight, Flame, Heart, Shield, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HomePage = ({ onAuthClick }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API}/products`);
        setProducts(res.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  return (
    <div className="paper-texture">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1722931303388-527993417e23?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxhbWJlciUyMGhlcmJhbCUyMHRvbmljJTIwYm90dGxlJTIwcnVzdGljfGVufDB8fHx8MTc2Nzg5ODg1M3ww&ixlib=rb-4.1.0&q=85"
            alt="Fire Cider"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/40"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <span className="font-accent text-2xl text-primary animate-fade-in-up" data-testid="hero-tagline">Handcrafted with tradition</span>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-secondary mt-4 tracking-tight animate-fade-in-up animate-delay-100" data-testid="hero-title">
              Fire Cider
              <span className="block text-primary">for Vitality</span>
            </h1>
            <p className="text-lg text-muted-foreground mt-6 max-w-lg animate-fade-in-up animate-delay-200">
              Our traditional herbal tonic is made with organic apple cider vinegar 
              and a warming blend of roots, fruits, and spices. Boost your immune system naturally.
            </p>
            <div className="flex flex-wrap gap-4 mt-8 animate-fade-in-up animate-delay-300">
              <Link to="/products">
                <Button size="lg" className="text-lg px-8" data-testid="shop-now-btn">
                  Shop Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Button variant="outline" size="lg" className="text-lg px-8" data-testid="learn-more-btn">
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-accent text-xl text-primary">Why Choose Us</span>
            <h2 className="font-heading text-4xl font-bold text-secondary mt-2">The East Burford Difference</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Flame, title: 'Traditional Recipe', desc: 'Brewed using age-old methods passed down through generations' },
              { icon: Heart, title: 'Made with Love', desc: 'Small batch production ensures quality in every bottle' },
              { icon: Shield, title: 'Immune Support', desc: 'Natural ingredients to help support your body\'s defenses' },
              { icon: Sparkles, title: 'Organic Ingredients', desc: 'Sourced from trusted local farms and suppliers' },
            ].map((feature, i) => (
              <div key={i} className="bg-white p-8 rounded-xl border border-amber-100/50 hover:shadow-warm-lg transition-all duration-300 hover:-translate-y-1" data-testid={`feature-${i}`}>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-secondary">{feature.title}</h3>
                <p className="text-muted-foreground mt-2">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="font-accent text-xl text-primary">Our Products</span>
              <h2 className="font-heading text-4xl font-bold text-secondary mt-2">Featured Fire Ciders</h2>
            </div>
            <Link to="/products">
              <Button variant="outline" data-testid="view-all-products">
                View All
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-surface rounded-lg h-96 animate-pulse"></div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="featured-products">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} onAuthRequired={onAuthClick} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-secondary text-secondary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-accent text-2xl text-primary">Our Story</span>
              <h2 className="font-heading text-4xl font-bold mt-2">From Farm to Bottle</h2>
              <p className="text-amber-100/80 mt-6 text-lg leading-relaxed">
                Nestled in the rolling hills of the Cotswolds, East Burford Naturals was born from 
                a passion for traditional herbal remedies and a love of the land. Our fire cider 
                recipe has been perfected over years, combining the wisdom of folk herbalism with 
                modern wellness practices.
              </p>
              <p className="text-amber-100/80 mt-4 text-lg leading-relaxed">
                Every bottle is handcrafted in small batches, ensuring the highest quality and 
                potency. We source our ingredients from local organic farms, supporting our community 
                while delivering the best nature has to offer.
              </p>
              <p className="font-accent text-2xl text-primary mt-8">
                "Fire in every drop, nature in every sip"
              </p>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1766331160106-3c972c4b12ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxydXN0aWMlMjB3b29kZW4lMjBraXRjaGVuJTIwYWVzdGhldGljfGVufDB8fHx8MTc2Nzg5ODg1N3ww&ixlib=rb-4.1.0&q=85"
                alt="Rustic kitchen"
                className="rounded-xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-primary text-primary-foreground p-6 rounded-lg shadow-lg">
                <span className="font-accent text-3xl block">Est. 2020</span>
                <span className="text-sm">Small Batch • Organic</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="font-accent text-xl text-primary">Ready to ignite your wellness?</span>
          <h2 className="font-heading text-4xl font-bold text-secondary mt-2">Try Our Fire Cider Today</h2>
          <p className="text-muted-foreground mt-4 text-lg">
            Join thousands of happy customers who've made fire cider part of their daily wellness routine.
          </p>
          <Link to="/products">
            <Button size="lg" className="mt-8 text-lg px-10" data-testid="cta-shop-btn">
              Shop Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
